import nextTick from "../utils/next-tick";
import { getArtworkInfo, DEFAULT_MEDIUM, createBoundingBoxFromObject } from "../utils/artwork-model-utils";
import { findAncestorWithComponent } from "../utils/scene-graph";

import ThreeMeshUI from "three-mesh-ui";
import FontJSON from "../assets/fonts/Roboto-msdf.json";
import FontImage from "../assets/fonts/Roboto-msdf.png";
import { ASCI_CHARACTERS } from "../react-components/constants/artworks";

const cleanText = text => {
  if (!text) return "";
  const validCharacters = ASCI_CHARACTERS;
  return text.replace(new RegExp(`[^${validCharacters}]`, "g"), "");
};

AFRAME.registerComponent("artwork-info", {
  schema: {
    artworkInfo: { type: "string" },
    mediumName: { type: "string" },
    scaleX: { default: 1 },
    scaleY: { default: 1 },
    isDefaultMedium: { type: "boolean" },
    image: { type: "string" }
  },
  tick() {
    ThreeMeshUI.update();
  },
  init() {
    this.modelLoaded = false;
    this.cardInfoWidth = null;
  },
  update(oldData) {
    if (this.data.artworkInfo !== oldData.artworkInfo) {
      this.modelLoaded = false;
      this.createScene();
    }
    this.handlePresentedArtwork();
  },
  addLineBreak(text) {
    const MAX_LEN = 45;

    if (text.length > MAX_LEN) {
      const words = text.split(" ");
      let modifiedText = "";
      let currentLineLength = 0;

      for (let i = 0; i < words.length; i++) {
        if (currentLineLength + words[i].length > MAX_LEN) {
          modifiedText += "\n" + words[i] + " ";
          currentLineLength = words[i].length + 1;
        } else {
          modifiedText += words[i] + " ";
          currentLineLength += words[i].length + 1;
        }
      }

      return modifiedText.trim();
    }

    return text;
  },
  getLongestLine(text) {
    const lines = text.split("\n");
    let longestLine = "";

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length > longestLine.length) {
        longestLine = lines[i];
      }
    }

    return longestLine;
  },

  handlePresentedArtwork() {
    const ancestorEl = findAncestorWithComponent(this.el, "artwork");
    if (ancestorEl) {
      ancestorEl.components["artwork"].handlePresentedArtwork();
    }
  },
  linesQuantity(text) {
    const linesArray = text.split("\n");
    let lines;

    for (let i = 0; i < linesArray.length; i++) {
      lines++;
    }

    return lines;
  },
  async createScene() {
    const boxEntity = document.createElement("a-entity");
    this.el.appendChild(boxEntity);

    const artworkInfo = getArtworkInfo({
      medium: this.data.artworkInfo?.medium || this.data.mediumName,
      ...this.data.artworkInfo
    });

    this.artworkInfo = artworkInfo;

    const {
      author,
      description,
      authorBirthDeath,
      title,
      category,
      artworkDate,
      medium,
      dimensions,
      artistStatement,
      metadata
    } = this.artworkInfo;

    // Old logic to concat the artwork info, it has been replaced by Three Mesh UI in different blocks
    // This can be deleted and we can change the logic to calculate y-axis position
    let artworkInfoValue = author;

    if (authorBirthDeath) {
      artworkInfoValue += `\n${authorBirthDeath}`;
    }

    artworkInfo.description = `${title} ${artworkDate ? `(${artworkDate})` : ""}`;
    artworkInfoValue += `\n${artworkInfo.description}`;

    if (description?.length) {
      artworkInfoValue += `\n${this.addLineBreak(description)}`;
    }

    if (artistStatement?.length) {
      artworkInfoValue += `\n${this.addLineBreak(artistStatement)}`;
    }

    if (category) {
      artworkInfoValue += `\n${category}`;
    }

    if (medium && medium !== DEFAULT_MEDIUM) {
      artworkInfoValue += `\n${medium}`;
    }

    if (dimensions) {
      artworkInfoValue += `\n${dimensions}`;
    }

    const longestLine = this.getLongestLine(artworkInfoValue);
    const maxLength = longestLine.length;
    const minValue = 0.45;
    let labelWidth = 0.017 * maxLength;
    labelWidth = labelWidth < minValue ? minValue : labelWidth;

    // Count the number of lines in the text
    const lineCount = artworkInfoValue.split("\n").length;

    // Adjust the Y position based on the calculated height
    let valueY = (this.data.scaleY / 2) * -1 - 0.2;

    const extraSpacing = 0.02; // Spacing to add per additional line above 4
    if (lineCount > 6) {
      valueY -= extraSpacing * (lineCount - 6);
    }

    let valueX = this.data.scaleX / 2 + 0.45;

    if (this.data.isDefaultMedium) {
      valueX += 0.09;
    }

    const z = 0.02;

    const container = new ThreeMeshUI.Block({
      width: 0.9,
      height: 0.5,
      backgroundColor: new THREE.Color(0xffffff),
      justifyContent: "start",
      textAlign: "left",
      fontFamily: FontJSON,
      fontTexture: FontImage,
      backgroundOpacity: 1,
      hiddenOverflow: true
    });

    container.setupState({
      state: "hidden-on",
      attributes: { hiddenOverflow: true }
    });

    container.setState("hidden-on");

    const truncatedAuthor = author?.length > 37 ? author?.substring(0, 37) + "..." : author;

    const truncatedArtTitlte = title?.length > 37 ? title?.substring(0, 37) + "..." : title;

    const customMedium =
      medium == "Custom"
        ? metadata?.customMedium?.length > 37
          ? metadata?.customMedium?.substring(0, 37) + "..."
          : metadata?.customMedium
        : null;

    const authorContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.08,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedAuthor),
        fontSize: 0.04,
        fontColor: new THREE.Color(0x000000)
      })
    );

    const artTitleContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.07,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedArtTitlte),
        fontSize: 0.035,
        fontColor: new THREE.Color(0x000000)
      })
    );

    const artDateContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(artworkDate),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    const artMedium = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(customMedium || medium?.label || medium),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    const truncatedDimensions = dimensions?.length > 37 ? dimensions?.substring(0, 37) + "..." : dimensions;

    const artDimensions = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedDimensions),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    let descriptionMaxLen = 143;
    if (!medium && !dimensions) {
      descriptionMaxLen = 200;
    }

    const truncatedDescription = description?.length > 143 ? description?.substring(0, 140) + "..." : description;

    const artDescription = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      hiddenOverflow: true,
      backgroundColor: new THREE.Color(0xffffff)
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedDescription),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    container.add(authorContainer, artTitleContainer);
    if (artworkDate) {
      container.add(artDateContainer);
    }

    if (medium) {
      container.add(artMedium);
    }
    if (dimensions) {
      container.add(artDimensions);
    }
    if (description) {
      container.add(artDescription);
    }

    this.el.object3D.position.set(this.el.object3D.position.x, this.el.object3D.position.y, z);
    boxEntity.setObject3D("mesh", container);
    this.el.setAttribute("position", `${valueX} ${valueY} ${z}`);

    await nextTick();
    this.handlePresentedArtwork();
    if (this.data.image) {
      setTimeout(() => {
        this.resize2();
      }, 10);
    }
  },
  resize2() {
    let attempts = 0;
    const maxAttempts = 10;

    const checkScale = () => {
      if (this.el?.parentElement) {
        const scale = this.el.parentElement.object3DMap.mesh.scale;

        if (scale.x === 1 && scale.y === 1 && scale.z === 1) {
          const box = createBoundingBoxFromObject(this.el.parentElement.object3DMap.mesh);
          // if(!this.data.image) {
          const cardSizeBetween2 = 0.4;
          const objSize = (box.max.y - box.min.y) / 2 - cardSizeBetween2 / 2;
          let valueY = 0 - objSize;

          this.el.object3D.position.set(this.el.object3D.position.x, valueY, this.el.object3D.position.z);
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;

          setTimeout(checkScale, 1000); // Retry after 1 second
        } else {
        }
      }
    };

    checkScale();
  },

  createBoundingBoxFromObject(obj) {
    return new THREE.Box3().setFromObject(obj);
  }
});
